import { API_BASE_URL } from '../config';
import { createOwnerCameraArchiveApi } from './ownerCameraArchiveApi';

const invalid = () => new Error('Streams response could not be verified. Refresh to retry.');
const id = value => typeof value === 'string' && /^[a-f0-9]{24}$/.test(value);
const cursorValue = value => typeof value === 'string' && /^[A-Za-z0-9_-]{39,256}$/.test(value);
const shape = (value, keys) => value && typeof value === 'object' && !Array.isArray(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const date = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{3})?Z$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().replace('.000Z', 'Z') === value.replace('.000Z', 'Z');
export function readSharedStream(s, view) {
  if (!shape(s, ['id','ownerId','sourceDojoId','state','active','audiences','startedAt','observedLiveAt','endedAt','recording'])
    || !id(s.id) || !id(s.ownerId) || !(s.sourceDojoId === null || id(s.sourceDojoId))
    || !date(s.startedAt) || ![s.observedLiveAt, s.endedAt].every(d => d === null || date(d))
    || !['live','history'].includes(view)
    || !(view === 'live' ? s.active === true && ['ready','live'].includes(s.state) : s.active === false && ['ending','finalizing','ended','failed'].includes(s.state))
    || !Array.isArray(s.audiences) || s.audiences.length > 4
    || Array.from(s.audiences).some(a => !['public','friends','dojo','dojo_chief'].includes(a))
    || new Set(s.audiences).size !== s.audiences.length || (s.audiences.includes('public') && s.audiences.length !== 1)
    || !shape(s.recording, ['state','parts']) || !['pending','complete','empty','failed','partial'].includes(s.recording.state)
    || !Number.isSafeInteger(s.recording.parts) || s.recording.parts < 0
    || (s.recording.state === 'complete' && (s.state !== 'ended' || s.recording.parts === 0))) throw invalid();
  return Object.freeze({ ...s, audiences: Object.freeze([...s.audiences]), recording: Object.freeze({ ...s.recording }) });
}
export function readSharedStreamsPage(body, view, cursor = null) {
  if (!shape(body, ['sessions','nextCursor','exhausted']) || !Array.isArray(body.sessions) || body.sessions.length > 20
    || typeof body.exhausted !== 'boolean' || (body.exhausted ? body.nextCursor !== null : !cursorValue(body.nextCursor))
    || (cursor !== null && body.nextCursor === cursor)) throw invalid();
  const sessions = Array.from(body.sessions, row => readSharedStream(row, view));
  if (sessions.some((row, i) => i > 0 && row.id >= sessions[i - 1].id)) throw invalid();
  return { sessions, nextCursor: body.nextCursor, exhausted: body.exhausted };
}

// The server signs HS256 media capabilities; decoding here only checks consistency.
// Authentication and signature authority remain at the app/provider boundary.
export function readLiveGrant(body, sessionId, ownerId, mediaOrigin, now = Date.now()) {
  try {
    const origin = new URL(mediaOrigin);
    if (origin.pathname !== '/' || origin.username || origin.password || origin.search || origin.hash
      || !(origin.protocol === 'https:' || (origin.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(origin.hostname)))) throw invalid();
    if (!id(sessionId) || !shape(body, ['role', 'token', 'expiresAt', 'whepUrl']) || body.role !== 'read'
      || !date(body.expiresAt) || Date.parse(body.expiresAt) <= now
      || typeof body.token !== 'string' || body.token.length > 4096
      || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(body.token)
      || body.whepUrl !== `${origin.origin}/cam-${sessionId}/whep`) throw invalid();
    const decode = part => JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const [header, claims] = body.token.split('.').slice(0, 2).map(decode);
    if (header.alg !== 'HS256' || claims.role !== 'read' || claims.sid !== sessionId
      || claims.sub !== ownerId || claims.path !== `cam-${sessionId}`
      || !Number.isSafeInteger(claims.exp) || claims.exp * 1000 !== Date.parse(body.expiresAt)) throw invalid();
    return Object.freeze({ endpoint: body.whepUrl, token: body.token, expiresAt: Date.parse(body.expiresAt) });
  } catch { throw invalid(); }
}

export function createSharedCameraStreamsApi(options) {
  const { backendService, identity, ownerId, mediaOrigin, isCurrent = () => true, timeoutMs = 10000 } = options;
  // Narrow composition: these authenticated readers do not apply the owner's
  // list parser. The owner adapter and its DELETE authority are unchanged.
  const { parts, media, assertCurrent } = createOwnerCameraArchiveApi({ ...options, timeoutMs });
  const base = new URL(API_BASE_URL || window.location.origin).origin + '/api/camera/discovery';
  return Object.freeze({ parts, media, assertCurrent,
    async live(sessionId, signal) {
      if (!id(sessionId)) throw invalid();
      assertCurrent();
      if (signal?.aborted) throw invalid();
      const controller = new AbortController();
      let rejectCancelled;
      const cancelled = new Promise((_, reject) => { rejectCancelled = reject; });
      const cancel = () => { controller.abort(); rejectCancelled(invalid()); };
      signal?.addEventListener('abort', cancel, { once: true });
      const timer = setTimeout(cancel, timeoutMs);
      const url = new URL(base).origin + `/api/camera/sessions/${sessionId}/viewer-token`;
      try {
        const response = await Promise.race([backendService.client.request({ method: 'post', url,
          signal: controller.signal, timeout: timeoutMs, withCredentials: false,
          katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null,
        }), cancelled]);
        assertCurrent();
        // XHR's final response URL confirms that authority did not redirect.
        if (controller.signal.aborted || response.status !== 200 || response.request?.responseURL !== url) throw invalid();
        return readLiveGrant(response.data, sessionId, ownerId, mediaOrigin);
      } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
    },
    async list(view, cursor = null, signal) {
      if (!['live','history'].includes(view) || (cursor !== null && !cursorValue(cursor))) throw invalid();
      assertCurrent();
      if (!isCurrent() || signal?.aborted) throw new Error('Streams request cancelled.');
      const controller = new AbortController();
      let rejectCancelled, timer;
      const cancelled = new Promise((_, reject) => { rejectCancelled = reject; });
      const cancel = () => { controller.abort(); rejectCancelled(new Error('Streams request cancelled or timed out.')); };
      signal?.addEventListener('abort', cancel, { once: true });
      timer = setTimeout(cancel, timeoutMs);
      try {
        const response = await Promise.race([backendService.client.request({ method: 'get', url: base,
          params: { view, limit: 20, ...(cursor === null ? {} : { cursor }) },
          signal: controller.signal, timeout: timeoutMs, withCredentials: false,
          katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null,
        }), cancelled]);
        assertCurrent();
        if (!isCurrent() || controller.signal.aborted) throw new Error('Streams request cancelled.');
        if (response.status !== 200) throw invalid();
        return readSharedStreamsPage(response.data, view, cursor);
      } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
    },
  });
}
